import { isPositiveNumber, reduce } from "et3-type-guards";
import { memoize } from "lodash";
import { observable, runInAction } from "mobx";
import { getDefaultDate } from "../../../helpers/et3-date-helper";
import {
  TVIPKhattabatStatus,
  TKhattabatApplicationStatus,
  E_KHATTABAT_APPLICATION_STATUS,
  TGetKhattabaClients,
  TKhattabatConversations,
  TClientBreachLevelState
} from "../../../Types";
import getKhattabatSharedMessagingStore from "../messaging-stores/conversation-messaging-store";
import getKhattabat101CMessagingStore from "./101c-messaging-store";
import getKhattabat101MessagingStore from "../messaging-stores/101-messaging-store";

class Khattabat101CStore {
  userID = observable.box<number>(0);
  userUUID = observable.box<string>("");
  oneOOneCId = observable.box<string>("");
  oneOTwoConversationIds = observable.box<string[]>([]);
  isBlocked = observable.box<number>(0);
  isDeleted = observable.box<number>(0);
  createdOn = observable.box<string>("");
  isVIPKhatabat = observable.box<boolean>(false);
  userName = observable.box<string>("");
  originCountry = observable.box<string>("");
  residenceCoutry = observable.box<string>("");
  originCountryCode = observable.box<string>("");
  residenceCountryCode = observable.box<string>("");
  height = observable.box<number>(0);
  weight = observable.box<number>(0);
  age = observable.box<number>(0);
  marriageType = observable.box<string>("");
  isOneOOneCHiddenByKhattaba = observable.box<boolean>(false);
  isRenewedInLastSevenDays = observable.box<boolean>(false);
  isOneOOneCHasBeenReactivated = observable.box<number>(1);
  oneOOneCReactivateDate = observable.box<Date>(getDefaultDate());
  hasUnreadMessagesSinceMoreThan18Hours = observable.box<boolean>(false);
  hasNoRCMessageSinceMoreThan54Hours = observable.box<boolean>(false);
  clientIsAlmostExpiredAndNo102Quality = observable.box<boolean>(false);
  vipKhattabaStatus = observable.box<TVIPKhattabatStatus>("EXPIRED");
  oneOTwoCounter = observable.box<number>(0);
  shouldShowConversation = observable.box<boolean>(false);
  hasActiveOneOTwo = observable.box<boolean>(false);
  isInItroductoryPhase = observable.box<boolean>(false);
  isSilentBanned = observable.box<number>(0);
  lastLogin = observable.box<string>("");
  insertUserIdField = observable.box<number>(0);
  applicationStatus = observable.box<TKhattabatApplicationStatus>(
    E_KHATTABAT_APPLICATION_STATUS.NONE
  );
  hasNoActiveOneOTwo = observable.box<boolean>(false);
  userQuizStatusToBeUpdated = observable.box<{
    newStatus: TKhattabatApplicationStatus;
  }>({ newStatus: E_KHATTABAT_APPLICATION_STATUS.REJECTED });

  setClientData = (data: TGetKhattabaClients) => {
    runInAction(() => {
      this.isBlocked.set(reduce(() => data.isBlocked, 0));
      this.isDeleted.set(reduce(() => data.isDeleted, 0));
      this.createdOn.set(reduce(() => data.createdOn, ""));
      this.oneOOneCId.set(reduce(() => data.oneOOneCId, ""));
      this.userUUID.set(reduce(() => data.userUUID, ""));
      this.isVIPKhatabat.set(
        reduce(
          () => (data.vipAlkhattabatStatus === "EXPIRED" ? false : true),
          false
        )
      );
      this.vipKhattabaStatus.set(
        reduce(() => data.vipAlkhattabatStatus, "EXPIRED")
      );
      this.userName.set(reduce(() => data.userName, ""));
      this.originCountry.set(reduce(() => data.originCountry, ""));
      this.residenceCoutry.set(reduce(() => data.residenceCoutry, ""));
      this.originCountryCode.set(reduce(() => data.originCountryCode, ""));
      this.residenceCountryCode.set(
        reduce(() => data.residenceCountryCode, "")
      );
      this.height.set(reduce(() => data.height, 0));
      this.weight.set(reduce(() => data.weight, 0));
      this.age.set(reduce(() => data.age, 0));
      this.marriageType.set(reduce(() => data.marriageType, ""));

      getKhattabatSharedMessagingStore(
        this.oneOOneCId.get()
      ).setUnreadMessagesCounter(reduce(() => data.unreadOneOOneCMessages, 0));

      const oneOOneCId = this.oneOOneCId.get();
      const clientUserID = reduce(() => data.userID, 0);
      const clientUUID = reduce(() => data.userUUID, "");
      const clientName = reduce(() => data.userName, "");

      getKhattabatSharedMessagingStore(oneOOneCId).conversationType.set("101C");
      const messagingStore = getKhattabat101MessagingStore(oneOOneCId);
      messagingStore.receiverID.set(clientUserID);
      messagingStore.receiverUUID.set(clientUUID);
      messagingStore.receiverName.set(clientName);

      const commentsConversationID = `${oneOOneCId}_COMMENTS`;
      getKhattabatSharedMessagingStore(
        commentsConversationID
      ).conversationType.set("101C_COMMENTS");
      const commentsMessagingStore = getKhattabat101MessagingStore(
        commentsConversationID
      );
      commentsMessagingStore.receiverID.set(clientUserID);
      commentsMessagingStore.receiverUUID.set(clientUUID);
      commentsMessagingStore.receiverName.set(clientName);

      this.isOneOOneCHiddenByKhattaba.set(
        reduce(() => data.isOneOOneCHiddenByKhattaba, false)
      );
      this.isRenewedInLastSevenDays.set(
        reduce(() => data.isRenewedInLastSevenDays, false)
      );
      this.isOneOOneCHasBeenReactivated.set(
        reduce(() => data.hasBeenReactivated, 1)
      );
      this.oneOOneCReactivateDate.set(
        reduce(() => data.reactivatedAt, getDefaultDate())
      );
      this.hasUnreadMessagesSinceMoreThan18Hours.set(
        reduce(() => data.hasUnreadMessagesSinceMoreThan18Hours, false)
      );
      this.hasNoRCMessageSinceMoreThan54Hours.set(
        reduce(() => data.hasNoRCMessageSinceMoreThan54Hours, false)
      );
      this.clientIsAlmostExpiredAndNo102Quality.set(
        reduce(() => data.clientIsAlmostExpiredAndNo102Quality, false)
      );
      this.oneOTwoCounter.set(reduce(() => data.oneOTwoCounter, 0));
      this.hasActiveOneOTwo.set(reduce(() => data.hasActiveOneOTwo, false));
      this.isInItroductoryPhase.set(
        reduce(() => data.isInItroductoryPhase, false)
      );
      this.isSilentBanned.set(reduce(() => data.isSilentBanned, 0));
      this.lastLogin.set(reduce(() => data.lastLogin, ""));
      this.applicationStatus.set(
        reduce(
          () => data.applicationStatus,
          E_KHATTABAT_APPLICATION_STATUS.NONE
        )
      );
      this.hasNoActiveOneOTwo.set(
        !isPositiveNumber(this.oneOTwoCounter.get()) ||
          !this.hasActiveOneOTwo.get()
      );
    });
  };

  updateClientConversation = ({
    clientConversation
  }: {
    clientConversation: TKhattabatConversations;
  }) => {
    runInAction(() => {
      this.userUUID.set(reduce(() => clientConversation.userUUID, ""));
      this.oneOOneCId.set(reduce(() => clientConversation.oneOOneCId, ""));
      this.oneOTwoConversationIds.set(
        Object.keys(reduce(() => clientConversation.oneOTwoMessages, {}))
      );
      this.shouldShowConversation.set(true);
      getKhattabat101CMessagingStore(
        this.userID.get()
      ).updateClientConversation({
        clientConversation
      });
    });
  };
  closeClientConversation = () => {
    runInAction(() => {
      this.shouldShowConversation.set(false);
    });
  };

  updateBreachLevelStat = ({
    booleanKey,
    booleanKeyNewValue
  }: {
    booleanKey: TClientBreachLevelState;
    booleanKeyNewValue: boolean;
  }) => {
    runInAction(() => {
      this[booleanKey].set(booleanKeyNewValue);
    });
  };

  setKhattabaClientTobeReactivatedClientSide = (
    newReactivationAtDate: Date
  ) => {
    runInAction(() => {
      this.isOneOOneCHasBeenReactivated.set(1);
      this.oneOOneCReactivateDate.set(newReactivationAtDate);
    });
  };

  setUserQuizStatusToBeUpdated = ({
    newStatus
  }: {
    newStatus: TKhattabatApplicationStatus;
  }) => {
    runInAction(() => {
      this.userQuizStatusToBeUpdated.set({ newStatus });
    });
  };
}

const getKhattabat101CStore = memoize((userID: number) => {
  const store = new Khattabat101CStore();
  store.userID.set(userID);
  return store;
});

export default getKhattabat101CStore;
